import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AppShell, WallShell } from "@/shell/AppShell";
import ScreenBoundary from "@/shell/review/ScreenBoundary";
import { SCREENS } from "@/screens/registry";
import HomePage from "@/screens/home/HomePage";
import KitPage from "@/screens/kit/KitPage";
import SchedulePage from "@/screens/schedule/SchedulePage";
import CitiesBoardPage from "@/screens/cities/BoardPage";
import BrandOverviewPage from "@/screens/brands/BrandOverviewPage";
import EmailBoardPage from "@/screens/email/BoardPage";
import EmailZeroWallPage from "@/screens/email/EmailZeroWallPage";
import ResponseLoopPage from "@/screens/email/ResponseLoopPage";
import PhonePage from "@/screens/phone/PhonePage";
import CalendarBoardPage from "@/screens/calendar/BoardPage"; // brand-scoped /{brand}/calendar + Schedule tab embed
// Brands board page removed 10/21 (Jeff) — BrandOverviewPage stays (brand overviews).
// (Old /brands path redirected below.)
// (Old /calendar path redirected below — Calendar is now a tab on Schedule.)
import PeopleBoardPage from "@/screens/people/BoardPage";
import WorkBoardPage from "@/screens/work/BoardPage"; // P2/P3: the goal-axis page
import DeskPage from "@/screens/desk/DeskPage"; // P4: Home+Kit merged, one page
import Desk2Page from "@/screens/desk/Desk2Page"; // Jeff 10/30: faithful duplicate of the pre-v4 Desk
import ChatPage from "@/screens/chat/ChatPage"; // Jeff 2026-11-03: his running conversation with Kit
import NeedsMePage from "@/screens/needsme/NeedsMePage"; // 4→2 phase 1: what's blocked on my word
import InFlightPage from "@/screens/inflight/InFlightPage"; // 4→2 phase 1: where everything stands
import DocsPage from "@/screens/docs/DocsPage"; // P1: the workspace's document reader
import MusicPage from "@/screens/music/MusicPage"; // phase 2-3: the music library

const LegacyBoardRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/breakage/board${search}`} replace />;
};

// ── Legacy route redirects: canonical paths have moved under /tjb ──
const CitiesRedirect = () => <Navigate to="/tjb/cities" replace />;

const CitiesBoardRedirect = () => <Navigate to="/tjb/cities" replace />;

// Calendar is a tab on the Schedule page now (Jeff 2026-10-21). ?tab=calendar
// deep-links into the Calendar tab; /calendar redirects here.
const CalendarRedirect = () => <Navigate to="/schedule?tab=calendar" replace />;
const BrandsBoardRedirect = () => <Navigate to="/" replace />;

function ScheduleWithTab() {
  const { search } = useLocation();
  const tab =
    new URLSearchParams(search).get("tab") === "calendar"
      ? "calendar"
      : "automation";
  return <SchedulePage initialTab={tab} />;
}

// ── Brand overview components by key ──
const BRANDS = [
  { key: "tjb", slug: "truejoybirthing", path: "/tjb" },
  { key: "at", slug: "agentictrust", path: "/at" },
  { key: "to", slug: "trustoffice", path: "/to" },
  { key: "av", slug: "aeriusview", path: "/av" },
  { key: "wp", slug: "wingpoint", path: "/wp" },
  { key: "tm", slug: "trustminutes", path: "/tm" },
  { key: "sv", slug: "socializevideo", path: "/sv" },
  { key: "sd", slug: "stenodesk", path: "/sd" },
];

// Home is a workspace page in its own right — mounted at "/" and "/home".
// The other shell screens plug in from the registry; bare walls keep their /board routes.
const shellScreens = SCREENS.filter((s) => s.key !== "home");

// The route table is exported so the routing smoke test can mount it without
// the BrowserRouter (4→2 phase 1, council 2026-10-22).
export function AppRoutes() {
  return (
    <Routes>
        <Route element={<AppShell screens={SCREENS} />}>
          {/* Shell lanes get the boundary too (10/21): /schedule shipped with a
              render crash and only the registry routes were guarded, so the shell
              mounted an empty root. One bad screen must never blank the shell. */}
          <Route
            path="/"
            element={
              <ScreenBoundary>
                <DeskPage />
              </ScreenBoundary>
            }
          />
          <Route
            path="/desk"
            element={
              <ScreenBoundary>
                <DeskPage />
              </ScreenBoundary>
            }
          />
          <Route
            path="/desk2"
            element={
              <ScreenBoundary>
                <Desk2Page />
              </ScreenBoundary>
            }
          />
          {/* P4 shims: the Desk is the one page — old paths land there */}
          <Route path="/home" element={<Navigate to="/desk" replace />} />
          <Route path="/kit" element={<Navigate to="/desk" replace />} />
          {/* ── Chat (Jeff 2026-11-03): directly under Desk in the nav.
              Without this Route the path falls to the * catch-all and the
              page bounces back to home — the 11/03 bounce bug. ── */}
          <Route
            path="/chat"
            element={
              <ScreenBoundary>
                <ChatPage />
              </ScreenBoundary>
            }
          />
          <Route
            path="/schedule"
            element={
              <ScreenBoundary>
                <ScheduleWithTab />
              </ScreenBoundary>
            }
          />

          {/* ── Brand overview routes ── */}
          {BRANDS.map((b) => (
            <Route
              key={b.key}
              path={b.path}
              element={
                <ScreenBoundary>
                  <BrandOverviewPage brandKey={b.key} />
                </ScreenBoundary>
              }
            />
          ))}

          {/* ── TJB cities (canonical path) ── */}
          <Route
            path="/tjb/cities"
            element={
              <ScreenBoundary>
                <CitiesBoardPage />
              </ScreenBoundary>
            }
          />

          {/* ── Email — the Airmail-style client, global view (nav's Email link) ── */}
          <Route
            path="/email"
            element={
              <ScreenBoundary>
                <EmailBoardPage />
              </ScreenBoundary>
            }
          />

          {/* ── The zero-inbox wall at /email/board — the count Jeff trusts.
              The built wall (chunk GOALS/email-zero-inbox) mounts here: it
              reads the differential-gate-verified export emailZeroWall.json.
              Declared before the registry's WallShell pass so this Route
              wins over the shell screens' `${path}/board` entries. ── */}
          <Route
            path="/email/board"
            element={
              <ScreenBoundary>
                <EmailZeroWallPage />
              </ScreenBoundary>
            }
          />

          {/* ── The response-loop board — its own route, beside the client
              (/email) and the zero-inbox wall (/email/board). It is the
              coaching loop's surface: what went out, what Kit learned from
              your notes, and what is waiting on you. The note affordance
              on a sent row rides the standard note verb. ── */}
          <Route
            path="/email/loop"
            element={
              <ScreenBoundary>
                <ResponseLoopPage />
              </ScreenBoundary>
            }
          />

          {/* ── Phone — the call loop (Jeff 2026-10-22): calls coming in per
              brand line, where they came from, whether they were handled.
              Same Airmail structure as /email/loop; the committed redacted
              payload is the data source (transcripts stay machine-local). ── */}
          <Route
            path="/phone"
            element={
              <ScreenBoundary>
                <PhonePage />
              </ScreenBoundary>
            }
          />

          {/* ── Brand-scoped Email / Calendar / People ── */}
          {BRANDS.map((b) => (
            <Route
              key={`${b.key}-email`}
              path={`/${b.key}/email`}
              element={
                <ScreenBoundary>
                  <EmailBoardPage brandScope={b.key} />
                </ScreenBoundary>
              }
            />
          ))}
          {BRANDS.map((b) => (
            <Route
              key={`${b.key}-calendar`}
              path={`/${b.key}/calendar`}
              element={
                <ScreenBoundary>
                  <CalendarBoardPage brandScope={b.key} />
                </ScreenBoundary>
              }
            />
          ))}
          {BRANDS.map((b) => (
            <Route
              key={`${b.key}-people`}
              path={`/${b.key}/people`}
              element={
                <ScreenBoundary>
                  <PeopleBoardPage brandScope={b.key} />
                </ScreenBoundary>
              }
            />
          ))}

          {/* ── Brand-scoped Phone: the same board scoped to one line set ── */}
          {BRANDS.map((b) => (
            <Route
              key={`${b.key}-phone`}
              path={`/${b.key}/phone`}
              element={
                <ScreenBoundary>
                  <PhonePage brandScope={b.key} />
                </ScreenBoundary>
              }
            />
          ))}

          {/* ── Legacy redirects ── */}
          <Route path="/cities" element={<CitiesRedirect />} />
          <Route path="/cities/board" element={<CitiesBoardRedirect />} />
          {/* Calendar is a Schedule tab now (Jeff 2026-10-21); /brands board retired */}
          <Route path="/calendar" element={<CalendarRedirect />} />
          <Route path="/calendar/board" element={<CalendarRedirect />} />
          <Route path="/brands" element={<BrandsBoardRedirect />} />

          {/* ── 4→2 redirects (Jeff 2026-10-22): Work/Projects/Breakage folded.
              Jeff's links and muscle memory land on the two live pages; the bare
              walls stay reachable beneath via their /board routes. */}
          <Route path="/work" element={<Navigate to="/inflight" replace />} />
          <Route path="/projects" element={<Navigate to="/inflight" replace />} />
          <Route path="/breakage" element={<Navigate to="/inflight?section=monitors" replace />} />

          {/* ── 4→2 phase 1 (council 2026-10-22): the two composite pages ── */}
          {/* Needs Me — "what is blocked on my word?" The queue and the
              workshop tab; the tab lives in the URL (?tab=workshop). */}
          <Route
            path="/needsme"
            element={
              <ScreenBoundary>
                <NeedsMePage />
              </ScreenBoundary>
            }
          />
          {/* In Flight — "where does everything stand?" */}
          <Route
            path="/inflight"
            element={
              <ScreenBoundary>
                <InFlightPage />
              </ScreenBoundary>
            }
          />

          {/* ── The document reader (P1) — the workspace's own documents.
              It carries a parameter (/docs/:id) so the registry map cannot
              mount it: the reader's own routes are declared here. ── */}
          <Route
            path="/docs"
            element={
              <ScreenBoundary>
                <DocsPage />
              </ScreenBoundary>
            }
          />
          <Route
            path="/docs/:id"
            element={
              <ScreenBoundary>
                <DocsPage />
              </ScreenBoundary>
            }
          />

          {/* ── The music library (phase 2-3). The player bar rides the app
              shell, so the music survives every navigation — the library
              page only tells the player what to play. ── */}
          <Route
            path="/music"
            element={
              <ScreenBoundary>
                <MusicPage />
              </ScreenBoundary>
            }
          />
          {/* The library has no bare wall: the player lives in the shell, so
              /music/board lands on the library itself. */}
          <Route path="/music/board" element={<Navigate to="/music" replace />} />

          {/* ── Registry shell screens ── */}
          {/* Working pages at the primary paths (Jeff 2026-10-20: design-review
              pages are past tense — the board IS the page). The old review shells
              stay reachable at their /review path until they're retired. */}
          {shellScreens
            .filter(
              (s) =>
                s.key !== "needsme" /* routed above (4→2 phase 1) */ &&
                s.key !== "inflight" /* routed above (4→2 phase 1) */ &&
                s.key !== "chat" /* Chat has its own route above (Jeff 2026-11-03) */,
            )
            .map((s) => (
              <Route
                key={s.key}
                path={s.path}
                element={
                  <ScreenBoundary>
                    <s.Bare />
                  </ScreenBoundary>
                }
              />
            ))}
          {shellScreens
            .filter(
              (s) =>
                s.key !==
                "cities" /* /tjb/cities already routed above (Jeff 2026-10-21) */,
            )
            .map((s) => (
              <Route
                key={`${s.key}-review`}
                path={`${s.path}/review`}
                element={
                  <ScreenBoundary>
                    <s.Review />
                  </ScreenBoundary>
                }
              />
            ))}
        </Route>

        <Route element={<WallShell />}>
          {SCREENS.filter(
            (s) =>
              s.key !== "cities" /* /cities/board redirects to /tjb/cities */ &&
              s.key !== "people" /* people has no wall yet (soon) */ &&
              s.key !== "work" /* Work is page-shaped, not a wall (P3) */ &&
              s.key !== "needsme" /* page-shaped (4→2 phase 1) */ &&
              s.key !== "inflight" /* page-shaped (4→2 phase 1) */,
          ).map((s) => (
            <Route
              key={`${s.key}-bare`}
              path={`${s.path}/board`}
              element={
                <ScreenBoundary>
                  <s.Bare />
                </ScreenBoundary>
              }
            />
          ))}
        </Route>

        <Route path="/board" element={<LegacyBoardRedirect />} />

        {/* ── Full-slug brand aliases ── */}
        <Route
          path="/truejoybirthing/*"
          element={<Navigate to="/tjb" replace />}
        />
        <Route
          path="/truejoybirthing"
          element={<Navigate to="/tjb" replace />}
        />
        <Route path="/trustoffice/*" element={<Navigate to="/to" replace />} />
        <Route path="/trustoffice" element={<Navigate to="/to" replace />} />
        <Route path="/aeriusview/*" element={<Navigate to="/av" replace />} />
        <Route path="/aeriusview" element={<Navigate to="/av" replace />} />
        <Route path="/wingpoint/*" element={<Navigate to="/wp" replace />} />
        <Route path="/wingpoint" element={<Navigate to="/wp" replace />} />
        <Route path="/trustminutes/*" element={<Navigate to="/tm" replace />} />
        <Route path="/trustminutes" element={<Navigate to="/tm" replace />} />
        <Route
          path="/socializevideo/*"
          element={<Navigate to="/sv" replace />}
        />
        <Route path="/socializevideo" element={<Navigate to="/sv" replace />} />
        <Route path="/stenodesk/*" element={<Navigate to="/sd" replace />} />
        <Route path="/stenodesk" element={<Navigate to="/sd" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
